import { useState } from 'react';
import type { ReactNode } from 'react';
import { useI18n, LOCALES, type Locale } from '../i18n/index.tsx';
import { useTheme, type ThemePreference } from '../theme/index.tsx';
import type { MessageKey } from '../i18n/messages.ts';
import type { Session } from '../api/types.ts';
import type { AdminSection } from '../screens/Admin.tsx';
import { useCan } from '../session/index.tsx';
import './Shell.css';

/**
 * The application frame: brand, navigation, and the language and appearance
 * controls.
 *
 * The sidebar is the first child of a two-column grid. In a right-to-left
 * document the browser places the first column on the right, so §22's
 * "sidebar on the right in Persian" is a consequence of the direction rather
 * than a rule some component has to remember.
 */

export type Route =
  | { name: 'dashboard' }
  | { name: 'servers' }
  | { name: 'addServer' }
  | { name: 'deployments' }
  | { name: 'newDeployment' }
  | { name: 'deployment'; id: string }
  | { name: 'backups' }
  | { name: 'monitoring' }
  | { name: 'settings' }
  /**
   * A one-time link from the bot, opened by somebody with no account.
   *
   * Outside the shell entirely: no navigation, no session. It is listed here
   * because `parseRoute` returns it, not because it belongs to the signed-in
   * application.
   */
  | { name: 'access'; token: string }
  /**
   * The operator's area.
   *
   * A separate route rather than extra tabs inside Settings: it is not a
   * setting of this account, it is everybody's accounts, and putting it where
   * a customer's own preferences live would be the wrong shape even for the
   * one person who can see it.
   */
  | { name: 'admin'; section: AdminSection; customerId?: string };

/**
 * The whole of the navigation, in the order a customer meets it: where they
 * are, what they own, what they installed, what protects it, how it is doing,
 * and the things they set once.
 *
 * Six entries and no submenus. Certificates, DNS tokens, notification
 * preferences and the technical-detail switch all live inside Settings rather
 * than as siblings here: none of them is somewhere a person goes, they are
 * things a person changes, and a navigation that lists every one of them turns
 * the product into a control panel for itself.
 */
const NAV: readonly { route: Route['name']; label: MessageKey; icon: ReactNode }[] = [
  { route: 'dashboard', label: 'nav.dashboard', icon: <IconHome /> },
  { route: 'servers', label: 'nav.servers', icon: <IconServer /> },
  { route: 'deployments', label: 'nav.deployments', icon: <IconStack /> },
  { route: 'backups', label: 'nav.backups', icon: <IconArchive /> },
  { route: 'monitoring', label: 'nav.monitoring', icon: <IconPulse /> },
  { route: 'settings', label: 'nav.settings', icon: <IconSliders /> },
];

export function Shell({
  session,
  route,
  onNavigate,
  onSignOut,
  children,
}: {
  session: Session;
  route: Route;
  onNavigate: (route: Route) => void;
  onSignOut: () => void;
  children: ReactNode;
}): ReactNode {
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  // Support accounts can see an account without being able to change it, so
  // the one button that starts work is the one that must not be offered to
  // them (`session/index.tsx`).
  const canDeploy = useCan('deployment.create');
  /*
   * The one navigation entry that is not about this account.
   *
   * Driven by the session's own answer rather than by a role: no role grants
   * platform administration, and every Telegram customer is an owner. Hiding
   * it is a courtesy — `/api/admin/*` refuses anybody else regardless — but a
   * menu item that 403s is a menu item that should not be there.
   */
  const isPlatformAdmin = session.platformAdmin;

  // A menu that survives navigation covers the page the customer just asked
  // for, so every path that navigates closes it.
  const go = (next: Route): void => {
    setMenuOpen(false);
    onNavigate(next);
  };

  return (
    <div className="shell">
      <a className="shell__skip" href="#main">
        {t('nav.menu')}
      </a>

      <header className="shell__topbar">
        <button
          type="button"
          className="shell__menu-button"
          aria-expanded={menuOpen}
          aria-controls="shell-nav"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <IconMenu />
          <span className="visually-hidden">{menuOpen ? t('nav.close') : t('nav.menu')}</span>
        </button>
        <Brand />
        <div className="shell__topbar-spacer" />
        <Preferences />
      </header>

      <nav
        id="shell-nav"
        className={['shell__sidebar', menuOpen ? 'is-open' : ''].filter(Boolean).join(' ')}
        aria-label={t('nav.menu')}
      >
        <div className="shell__sidebar-brand">
          <Brand />
        </div>

        <ul className="shell__nav">
          {NAV.map((item) => (
            <li key={item.route}>
              <button
                type="button"
                className={['shell__nav-item', route.name === item.route ? 'is-active' : '']
                  .filter(Boolean)
                  .join(' ')}
                aria-current={route.name === item.route ? 'page' : undefined}
                onClick={() => go({ name: item.route } as Route)}
              >
                {item.icon}
                <span>{t(item.label)}</span>
              </button>
            </li>
          ))}
        </ul>

        {isPlatformAdmin ? (
          <ul className="shell__nav shell__nav--admin">
            <li>
              <button
                type="button"
                className={['shell__nav-item', route.name === 'admin' ? 'is-active' : '']
                  .filter(Boolean)
                  .join(' ')}
                aria-current={route.name === 'admin' ? 'page' : undefined}
                onClick={() => go({ name: 'admin', section: 'dashboard' })}
              >
                <IconShield />
                <span>{t('nav.admin')}</span>
              </button>
            </li>
          </ul>
        ) : null}

        {canDeploy ? (
          <div className="shell__sidebar-cta">
            <button
              type="button"
              className="shell__cta"
              onClick={() => go({ name: 'newDeployment' })}
            >
              <IconPlus />
              <span>{t('nav.newDeployment')}</span>
            </button>
          </div>
        ) : null}

        <div className="shell__sidebar-foot">
          <div className="shell__account">
            <span className="shell__avatar" aria-hidden="true">
              {session.user.displayName.trim().slice(0, 1).toUpperCase()}
            </span>
            <div className="shell__account-text">
              <strong>{session.user.displayName}</strong>
              <span className="subtle ltr-text">{session.user.email}</span>
            </div>
          </div>
          <button type="button" className="shell__nav-item" onClick={onSignOut}>
            <IconExit />
            <span>{t('nav.signOut')}</span>
          </button>
        </div>
      </nav>

      {menuOpen ? (
        <button
          type="button"
          className="shell__scrim"
          aria-label={t('nav.close')}
          onClick={() => setMenuOpen(false)}
        />
      ) : null}

      <main id="main" className="shell__main">
        <div className="shell__content">{children}</div>
      </main>
    </div>
  );
}

export function Brand(): ReactNode {
  const { t } = useI18n();
  return (
    <span className="brand">
      <span className="brand__mark" aria-hidden="true" />
      <span className="brand__name">{t('app.name')}</span>
    </span>
  );
}

/** Language and appearance, available on every screen including sign-in. */
export function Preferences(): ReactNode {
  const { t, locale, setLocale } = useI18n();
  const { preference, setPreference } = useTheme();

  return (
    <div className="preferences">
      <label className="preferences__control">
        <span className="visually-hidden">{t('locale.label')}</span>
        <IconGlobe />
        <select value={locale} onChange={(event) => setLocale(event.target.value as Locale)}>
          {LOCALES.map((value) => (
            // Each language names itself in itself: somebody looking for
            // Persian is looking for "فارسی", not for "Persian".
            <option key={value} value={value}>
              {value === 'fa' ? 'فارسی' : 'English'}
            </option>
          ))}
        </select>
      </label>

      <label className="preferences__control">
        <span className="visually-hidden">{t('theme.label')}</span>
        <IconTheme />
        <select
          value={preference}
          onChange={(event) => setPreference(event.target.value as ThemePreference)}
        >
          <option value="system">{t('theme.system')}</option>
          <option value="light">{t('theme.light')}</option>
          <option value="dark">{t('theme.dark')}</option>
        </select>
      </label>
    </div>
  );
}

/* Icons are inline so the interface needs no icon font and no extra request.
   `currentColor` keeps them correct in both themes without duplication. */

function IconHome(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path
        d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4v-5h-6v5H5a1 1 0 0 1-1-1z"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconServer(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="7" rx="2" />
      <rect x="3" y="13" width="18" height="7" rx="2" />
      <path d="M7 7.5h.01M7 16.5h.01" strokeLinecap="round" />
    </svg>
  );
}

function IconStack(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path d="m12 3 9 5-9 5-9-5z" strokeLinejoin="round" />
      <path d="m3 13 9 5 9-5" strokeLinejoin="round" />
    </svg>
  );
}

function IconArchive(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="4.5" rx="1.2" />
      <path d="M5 8.5V19a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8.5" strokeLinejoin="round" />
      <path d="M10 12h4" strokeLinecap="round" />
    </svg>
  );
}

function IconPulse(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path d="M3 12h4l2.5-6 4 13L16 12h5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconSliders(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path d="M4 7h10M18 7h2M4 17h4M12 17h8" strokeLinecap="round" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="10" cy="17" r="2" />
    </svg>
  );
}

function IconPlus(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
    </svg>
  );
}

function IconExit(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" strokeLinecap="round" />
      <path d="M10 8 6 12l4 4M6 12h9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconMenu(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      aria-hidden="true"
    >
      <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
    </svg>
  );
}

function IconGlobe(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.2 2.4 3.3 5.4 3.3 8.5S14.2 18.1 12 20.5c-2.2-2.4-3.3-5.4-3.3-8.5S9.8 5.9 12 3.5z" />
    </svg>
  );
}

function IconTheme(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4.2" />
      <path
        d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.2 5.2l1.4 1.4M17.4 17.4l1.4 1.4M18.8 5.2l-1.4 1.4M6.6 17.4l-1.4 1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconShield(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="icon">
      <path
        d="M12 3l7 3v6c0 4-3 7.5-7 9-4-1.5-7-5-7-9V6l7-3z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}
